import { type OpenGraph } from 'next-seo/lib/types';
import { memo, useMemo } from 'react';

import { type GetByShortIDCollectionResponse } from '@air/api';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicPageSeoTitle } from '~/components/v2/components/shared/seo/PublicPageSeoTitle';
import { getPublicShowcaseOgImageUrl } from '~/components/v2/components/showcase/getPublicShowcaseOgImageUrl';
import { createPublicShowcaseRoute } from '~/constants/routes';
import { getShareSeoDescription } from '~/swr-hooks/shortUrl/utils';
import { getPageUrl } from '~/utils/Url';

export type PublicShowcasePageSeoTitleProps = {
  // `thumbnail` is optional because not every document-owning surface carries one: the live /a page
  // passes the public record (thumbnail included), while the Preview iframe renders from the private
  // collection fetch, which has no thumbnail — its SEO card simply renders without an og:image
  // unless a cover/header is set.
  collection: Pick<GetByShortIDCollectionResponse['data'], 'name'> &
    Partial<Pick<GetByShortIDCollectionResponse['data'], 'thumbnail' | 'description'>>;
};

export const PublicShowcasePageSeoTitle = memo(({ collection }: PublicShowcasePageSeoTitleProps) => {
  const { presentationSettings, shortId } = useShortIdContext();
  const { name, thumbnail, description } = collection;
  const origin = getPageUrl();
  const coverImage = presentationSettings?.coverImage;

  const url = useMemo(() => `${origin}${createPublicShowcaseRoute(shortId)}`, [origin, shortId]);

  const ogImageUrl = useMemo(
    () => getPublicShowcaseOgImageUrl({ coverImage, origin, thumbnail }),
    [coverImage, origin, thumbnail],
  );

  const openGraph: OpenGraph = useMemo(
    () => ({
      url,
      images: ogImageUrl
        ? [
            {
              url: ogImageUrl,
              alt: `The preview image for '${name}' showcase`,
            },
          ]
        : undefined,
    }),
    [name, ogImageUrl, url],
  );

  return (
    <PublicPageSeoTitle
      canonical={url}
      description={getShareSeoDescription(description)}
      title={name}
      openGraph={openGraph}
    />
  );
});

PublicShowcasePageSeoTitle.displayName = 'PublicShowcasePageSeoTitle';
